/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {TranslateParser, TranslateService} from '@ngx-translate/core';

import {forkJoin, from, Observable, of, Subject} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';

import * as moment from 'moment/moment';

import {AbsenceEvent, CalendarAbsenceComponent} from './calendar/calendar-absence/calendar-absence.component';
import {
  COLOR1,
  COLOR2,
  COLOR3,
  COLOR4,
  COLOR5,
  COLOR6,
  COLOR7,
  COLOR8,
  getRangesForEstimation,
  scale,
  WHITE
} from './time-and-attendance-utils';
import {TimeAndAttendanceService} from './time-and-attendance.service';

import {ToastrService} from 'ngx-toastr';
import {LeaveRequestData} from '../../../+ess/employee-self.service';
import {
  ACCRUAL_STATUS_CONFIG_NOT_FOUND,
  ACCRUAL_STATUS_CONFIG_NOT_USABLE,
  ACCRUAL_STATUS_ESTIMATED,
  ACCRUAL_STATUS_ESTIMATING,
  ACCRUAL_STATUS_OUT_OF_RANGE,
  ACCRUAL_STATUS_PREPARE_ESTIMATION,
  DATE_FORMAT,
  ProfileMode
} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {CurrentEvent, DateRange} from '../../../models/time-and-attendance.interfaces';
import {Contract, Employee} from '../../../shared/employees/employees.model';
import {DataTransformer} from '../../../shared/form-generator/data-transformer/data-transformer';
import {FieldType} from '../../../shared/form-generator/fields/fields.interfaces';
import {
  GroupZoneDescriptor,
  ZoneDescriptor,
  ZoneValues
} from '../../../shared/form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../../../shared/form-generator/form-generator.service';
import {
  GeneratedSimpleFormComponent
} from '../../../shared/form-generator/generated-simple-form/generated-simple-form.component';
import {Validators as GpValidators} from '../../../shared/forms';
import {ModalCommand, ModalService, ModalState} from '../../../shared/modal';
import {DatePickerInputComponent} from '../../../shared/ui';
import {SortKey} from '../../../shared/ui/table/table.interfaces';
import {User, UserService} from '../../../shared/user';
import {groupBy} from '../../../shared/utils/arrays';
import {
  addMonths,
  addWeeks,
  addYears,
  range,
  startOfMonth,
  strToDate,
  subMonths,
  subWeeks,
  subYears
} from '../../../shared/utils/date';
import {isPresent} from '../../../shared/utils/lang';
import {at} from '../../../shared/utils/objects';
import {EmployeeVariablesService} from '../variable-elements/employee-variables.service';

export const zoneValueNotFilled = (zoneValues: ZoneValues, zone: ZoneDescriptor) => {
  const value = zoneValues.values[zone.zoneID].value;
  return !isValueFilled(value);
};

export const isValueFilled = (value: any) => {
  return value !== '' && value !== null && value !== undefined && value !== 0 && !Object.is(NaN, value);
};

@Component({
  selector: 'gp-payroll-prepare-time-and-attendance',
  templateUrl: './time-and-attendance.component.html',
  styleUrls: [
    '../../../../assets/fullcalendar/fullcalendar.theme.scss',
    './time-and-attendance.component.scss'
  ],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class TimeAndAttendanceComponent extends Destroyable implements OnChanges, OnInit {

  @Input()
  public isEss: boolean = false;

  @Input()
  public isReadOnly: boolean = true;

  @Input()
  public contract: Contract;

  @Input()
  public employee: Employee;

  @Input()
  public cliRegId: number;

  @ViewChild('input')
  public input: any;

  public view: string;
  public viewDate: any;
  public loading: boolean = false;
  public sortKey: SortKey = [];
  public absences: Array<any> = [];
  public presences: Array<any> = [];
  public parentTypes: { [name: string]: string } = {};

  public pendingRequests: any[] = [];
  public pendingLoading: boolean = true;

  public currentEvent: CurrentEvent;
  public eventTypes: any;
  public repAbsenceGroupZoneDescriptor: GroupZoneDescriptor;
  public repAbsenceZoneValues: ZoneValues;

  @ViewChild(GeneratedSimpleFormComponent)
  public generatedSimpleForm: GeneratedSimpleFormComponent;

  public types: any;
  public groupedTypes: any;
  public categorySelectList: { transId: string; id: string }[];
  public submitCallback: () => Observable<ModalCommand>;
  public workedHours: number = 8;

  public workCycleLoaded: boolean = false;
  public workCycleIsSpecified: boolean = false;
  public rangeStep: number = 0.1;
  public workcycle: any[] = [];
  public workcycleMap: Map<string, any> = new Map<string, any>();
  public leftReport: any;
  public rightReport: any;
  public eventForm: UntypedFormGroup;
  public isBeginBeforeEnd: boolean = true;
  public currentPayPeriod: any;
  public nextPayPeriod: any;
  public isMaximumTime: boolean = false;
  public isLongFuture: boolean = false;
  public payPeriodId: number | undefined;
  public minDate: Date;
  public maxDate: Date;
  public isSliderLoaded: boolean = false;
  public timeFields: any = {
    singleDateRange: {
      minutes: 0,
      hours: 0
    },
    beginDateRange: {
      minutes: 0,
      hours: 0
    },
    endDateRange: {
      minutes: 0,
      hours: 0
    }
  };

  @ViewChild('begindate')
  public beginDate: DatePickerInputComponent;

  @ViewChild('enddate')
  public endDate: DatePickerInputComponent;

  public formatter: any = {
    from(value: string): number {
      return +value;
    },
    to(value: number): string {
      return `${value}`;
    }
  };

  public accrualConfig: any;
  public lastClosedPayPeriod: any;
  public canTakeLeaveInPastError: boolean;

  public preview: boolean;

  private newEvents: Set<any> = new Set();
  private modifiedEvents: Set<any> = new Set();
  private deletedEvents: Set<any> = new Set();

  private cliCoreID: number;

  private debugAccrualEstimation: boolean;
  fromCategoryChangeEvent: boolean;
  fromChangeTypeEvent: boolean;
  private isEventTypeFilled: boolean;

  constructor(public employeeVariablesService: EmployeeVariablesService,
              public timeAndAttendenceService: TimeAndAttendanceService,
              public modalService: ModalService,
              public translate: TranslateService,
              public translateParser: TranslateParser,
              private toastrService: ToastrService,
              public cd: ChangeDetectorRef,
              private formBuilder: UntypedFormBuilder,
              private userService: UserService,
              private formGeneratorService: FormGeneratorService) {
    super();
    this.debugAccrualEstimation = localStorage.getItem('gp_debug_accrual_estimation') === 'true';
  }

  public ngOnInit() {
    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((user: User) => this.cliCoreID = user.cliCoreId);

    this.createEventForm();
    this.changeView('month');

    this.submitCallback = this.onSubmit.bind(this);

    this.translate.onLangChange
      .asObservable()
      .pipe(
        takeUntil(this.componentDestroyed$),
        map(() => this.computeNeededTransValues()),
        switchMap(neededTransIds => {
          if (neededTransIds && neededTransIds.length > 0) {
            return this.translate.get(neededTransIds);
          }
          return of(null);
        }),
        map(transValues => this.sortByTransValues(transValues))
      )
      .subscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!this.cliRegId) {
      return;
    }

    this.resetChanges();

    if (changes.contract) {
      this.payPeriodId = undefined;
      this.initData();
    } else {
      this.fetchTableData();
    }
  }

  public fetchTableData() {
    this.workCycleLoaded = false;

    if (!this.isEss) {
      this.leftReport = null;
      this.getRepReportTemplate('RepAccu1');
    }

    this.rightReport = null;
    this.getRepReportTemplate(this.isEss ? 'RepAccu3' : 'RepAccu2');

    this.getEventTypes().subscribe(() => {
      this.getCalendarData();
      this.checkStartDateContract();
      this.getPendingRequests();
    });
  }

  public getPendingRequests() {
    if (!this.isEss) {
      return;
    }
    this.pendingLoading = true;

    this.timeAndAttendenceService.getPendingRequests(this.contract.id, this.eventTypes)
      .pipe(finalize(() => {
        this.pendingLoading = false;
      }))
      .subscribe((data: any[]) => {
        this.pendingRequests = data;
      });
  }

  public changeView(view: string) {
    this.view = view;
    if (this.view === 'month') {
      this.viewDate = startOfMonth(this.viewDate);
      this.getCalendarData();
    }
  }

  public increment() {
    this.canNavigate()
      .pipe(filter((canNavigate: boolean) => canNavigate))
      .subscribe(() => {
        const addFn: any = {
          week: addWeeks,
          month: addMonths
        }[this.view];
        this.viewDate = this.view === 'month' ?
          startOfMonth(addFn(this.viewDate, 1)) :
          addFn(this.viewDate, 1);

        this.getCalendarData();
      });
  }

  public decrement() {
    this.canNavigate()
      .pipe(filter((canNavigate: boolean) => canNavigate))
      .subscribe(() => {
        const subFn: any = {
          week: subWeeks,
          month: subMonths
        }[this.view];
        this.viewDate = this.view === 'month' ?
          startOfMonth(subFn(this.viewDate, 1)) :
          subFn(this.viewDate, 1);

        this.getCalendarData();
      });
  }

  public incrementYear() {
    this.canNavigate()
      .pipe(filter((canNavigate: boolean) => canNavigate))
      .subscribe(() => {
        this.viewDate = moment(startOfMonth(addYears(this.viewDate, 1))).toDate();
        this.getCalendarData();
      });
  }

  public decrementYear() {
    this.canNavigate()
      .pipe(filter((canNavigate: boolean) => canNavigate))
      .subscribe(() => {
        this.viewDate = moment(startOfMonth(subYears(this.viewDate, 1))).toDate();
        this.getCalendarData();
      });
  }

  public onChangeDates() {
    if (!this.isInWorkCycle()) {
      if (!this.isAccrualEstimating(this.currentEvent)) {
        this.currentEvent.duration = {
          before: 0,
          current: 0,
          after: 0,
          status: ACCRUAL_STATUS_PREPARE_ESTIMATION
        };
      }
      this.getWorkCycle({
        begin: this.currentEvent.attributes.begin,
        end: this.currentEvent.attributes.end
      })
        .subscribe(() => this.onChangeDates());
      return;
    }

    if (this.isEss) {
      this.canTakeLeaveInPastError = moment(this.currentEvent.attributes.begin)
        .isBefore(moment().add(-1, 'months').startOf('day'));
    }

    this.isSliderLoaded = true;
    setTimeout(() => {
      this.isSliderLoaded = false;
    });
    this.checkDates();
    this.checkInputValue();
    this.refreshAccrual(this.currentEvent.attributes.type);
    this.setDateRangeTime('endDateRange', this.endDateRange, 'end');
  }

  public createCurrentEvent(event: any) {
    if (event) {
      this.currentEvent = cloneDeep(event);
    } else {
      this.currentEvent = {
        type: '',
        attributes: {
          begin: this.currentEvent.attributes.begin,
          end: this.currentEvent.attributes.end
        },
        singleDateRange: [],
        beginDateRange: [],
        endDateRange: [],
        comment: ''
      };
      this.createEventForm();
    }
    const attrs = this.currentEvent.attributes;
    attrs.empContract = this.contract.id;
    this.resetDateRanges(attrs);
    attrs.begin = attrs.begin || attrs.begindate;
    attrs.end = attrs.end || attrs.enddate;
    attrs.type = attrs.type || attrs.name;

    const currentEventType = this.eventTypes.find(eventType => eventType.name === attrs.type);
    if (currentEventType) {
      attrs.allowedDurationType = currentEventType.allowedDurationType;
    }

    this.currentEvent.parentType = this.parentTypes[attrs.type];
  }

  public eventClicked(data: any) {
    if (data.action === 'delete') {
      this.deleteEvent(data.event);
    } else {
      this.preview = data.action === 'view';
      this.createCurrentEvent(data.event);
      this.isMaximumTime = false;
      this.modalService.openModalWithId('edit-event');
      switch (data.action) {
        case CalendarAbsenceComponent.RESIZE_ACTION_INCREASE_END:
          this.currentEvent.attributes.end = moment(this.currentEvent.attributes.end)
            .add(1, 'days').format(DATE_FORMAT);
          break;
        case CalendarAbsenceComponent.RESIZE_ACTION_DECREASE_END:
          this.currentEvent.attributes.end = moment(this.currentEvent.attributes.end)
            .add(-1, 'days').format(DATE_FORMAT);
          break;
        case CalendarAbsenceComponent.RESIZE_ACTION_INCREASE_START:
          this.currentEvent.attributes.begin = moment(this.currentEvent.attributes.begin)
            .add(1, 'days').format(DATE_FORMAT);
          break;
        case CalendarAbsenceComponent.RESIZE_ACTION_DECREASE_START:
          this.currentEvent.attributes.begin = moment(this.currentEvent.attributes.begin)
            .add(-1, 'days').format(DATE_FORMAT);
          break;
      }
    }
  }

  public onChangeTypeEvent(absenceType: string) {
    this.isEventTypeFilled = absenceType?.length > 0;
    if (!this.fromCategoryChangeEvent) {
      this.fromChangeTypeEvent = true;
    }

    this.getRepAbsenceZones(absenceType, this.isEss);
    if (!this.isAccrualEstimating(this.currentEvent)) {
      this.currentEvent.duration = {
        before: 0,
        current: 0,
        after: 0,
        status: ACCRUAL_STATUS_PREPARE_ESTIMATION
      };
    }
    this.refreshAccrual(absenceType);
    this.changeCurrentEvent(absenceType);

    if (!this.fromCategoryChangeEvent) {
      this.eventForm.patchValue({familyType: this.currentEvent.familyTransId});
    }

    this.isSliderLoaded = true;
    setTimeout(() => {
      this.isSliderLoaded = false;
    });
    this.fromChangeTypeEvent = false;
  }

  public changeCurrentEvent(absenceType: string) {
    const newEvent = this.eventTypes.find(event => event.name === absenceType);

    if (newEvent && this.currentEvent.attributes.transid !== newEvent.attributes.transId) {
      this.currentEvent.type = newEvent.attributes.name;
      this.currentEvent.parentType = this.parentTypes[absenceType];

      this.currentEvent.familyTransId = newEvent.familyTransId;
      this.currentEvent.attributes.allowedDurationType = newEvent.allowedDurationType;
      this.currentEvent.attributes.beginDayfactor = 0;
      this.currentEvent.attributes.endDayfactor = 0;
      this.currentEvent.attributes.unitTransId = newEvent.attributes.unitTransId;
      this.resetDateRanges(this.currentEvent.attributes);
      if (this.currentEvent.attributes.comment) {
        this.currentEvent.attributes.comment = '';
      }
      this.currentEvent.attributes.transid = newEvent.attributes.transId;
      this.resetFilterSpecificValues();
    }
  }

  public resetDateRanges(attributes: any) {
    this.currentEvent.singleDateRange = [attributes.beginDayfactor || 0, 1 - (attributes.endDayfactor || 0)];
    this.setDateRangeTime('singleDateRange', this.currentEvent.singleDateRange, 'begin');
    this.currentEvent.beginDateRange = [attributes.beginDayfactor || 0, 1];
    this.setDateRangeTime('beginDateRange', this.currentEvent.beginDateRange, 'begin');
    this.currentEvent.endDateRange = [0, 1 - (attributes.endDayfactor || 0)];
    this.setDateRangeTime('endDateRange', this.currentEvent.endDateRange, 'end');
    if (this.currentEvent.singleDateRange[0] >= this.currentEvent.singleDateRange[1]) {
      this.currentEvent.singleDateRange[1] = 1;
    }
  }

  public getRepAbsenceZones(absenceType: string, isEss?: boolean) {
    if (!absenceType) {
      this.createCurrentEvent(null);
      return;
    }

    this.timeAndAttendenceService.getRepAbsenceZones(this.cliRegId, absenceType, isEss)
      .subscribe((zones: Array<ZoneDescriptor>) => {
        if (zones) {
          const groupZoneDescriptor = this.formGeneratorService.createEmptyGroupZoneDescriptor(absenceType, zones);
          groupZoneDescriptor.zones.forEach((zone: ZoneDescriptor) => {
            if (zone.type === 'FILE') {
              if (!zone.metadatas) {
                zone.metadatas = {};
              }
              zone.metadatas.contractId = this.contract.id;
            }
          });
          this.repAbsenceGroupZoneDescriptor = groupZoneDescriptor;
          const absence = this.currentEvent;
          const data = absence.attributes.values ? [absence.attributes] : [];
          this.repAbsenceZoneValues = DataTransformer.createZoneValues(groupZoneDescriptor, this.formGeneratorService, data, absence.id)[0];
          this.setDefaultValuesIfNecessary();
        } else {
          this.repAbsenceGroupZoneDescriptor = null;
          this.repAbsenceZoneValues = null;
        }
        this.cd.detectChanges();
      });
  }

  private setDefaultValuesIfNecessary(): void {
    const complexTypes: string[] = [FieldType.ENTITY, FieldType.ENTITY_DATA, FieldType.ENTITY_PREFIXED,
      FieldType.LIST, FieldType.TREE, FieldType.PEOPLE_TREE];
    for (const zone of this.repAbsenceGroupZoneDescriptor.zones) {
      if (zoneValueNotFilled(this.repAbsenceZoneValues, zone) && isValueFilled(zone.defaultValue)) {
        const defaultValue = zone.defaultValue;
        if (complexTypes.includes(zone.type)) {
          try {
            const complexDefaultValue = JSON.parse(defaultValue);
            if (!complexDefaultValue || !complexDefaultValue.value || !complexDefaultValue.transId) {
              throw new Error();
            }
            this.repAbsenceZoneValues.values[zone.zoneID] = complexDefaultValue;
          } catch (ignored) {
            console.warn('Failed to set default value on ' + zone.type + ' zone, bad format (value/transId required): ' + defaultValue);
          }
        } else {
          this.repAbsenceZoneValues.values[zone.zoneID].value = defaultValue;
        }
      }
    }
  }

  public isAccrualEstimating(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_ESTIMATING;
  }

  public isAccrualPrepareEstimation(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_PREPARE_ESTIMATION;
  }

  public isAccrualOutOfRange(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_OUT_OF_RANGE;
  }

  public isAccrualEstimated(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_ESTIMATED;
  }

  public isAccrualConfigNotFound(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_CONFIG_NOT_FOUND;
  }

  public isAccrualConfigNotUsable(event: CurrentEvent): boolean {
    return event && event.duration && event.duration.status === ACCRUAL_STATUS_CONFIG_NOT_USABLE;
  }

  public refreshAccrual(absenceType: string) {
    if (!this.isEss || this.isAccrualEstimating(this.currentEvent) || !this.cliRegId || !absenceType) {
      if (!this.isAccrualEstimating(this.currentEvent)) {
        this.currentEvent.duration = {
          before: 0,
          current: 0,
          after: 0,
          status: ACCRUAL_STATUS_CONFIG_NOT_USABLE
        };
      }
      return;
    }
    this.accrualConfig = null;
    this.currentEvent.duration = {
      before: 0,
      current: 0,
      after: 0,
      status: ACCRUAL_STATUS_ESTIMATING
    };

    if (!this.isBeginBeforeEnd || !this.getDatePickerValid()) {
      this.currentEvent.duration.status = ACCRUAL_STATUS_ESTIMATED;
      return;
    }

    this.timeAndAttendenceService.getAccrualData(this.cliRegId, absenceType, this.isEss ? ProfileMode.ESS : ProfileMode.CLASSIC)
      .pipe(
        switchMap((accrualDataResult: any) => {
          if (!accrualDataResult) {
            this.debugLog('[ACCRUAL] no configuration found for ' + absenceType);
            return of(null);
          }
          const response = accrualDataResult.data;
          this.accrualConfig = JSON.parse(response.attributes.template);
          this.accrualConfig.values = {};
          response.attributes.accus.split(';').forEach(accu => this.accrualConfig.values[accu] = '-');
          const payAccuNames: string = Object.keys(this.accrualConfig.values).map(name => `name=${name}`).join('&');
          this.debugLog('[ACCRUAL] configuration for ' + absenceType + ' fetched', this.accrualConfig);
          return this.timeAndAttendenceService.getLastClosedOnlyAccus(this.contract.id, payAccuNames);
        }),
        switchMap((lastClosedOnlyAccusResponse: any) => {
          if (lastClosedOnlyAccusResponse) {
            lastClosedOnlyAccusResponse.data.forEach(item => this.accrualConfig.values[item.attributes.name] = item.attributes.value);
          }
          this.debugLog('[ACCRUAL] last closed period pay values fetched', lastClosedOnlyAccusResponse);
          return this.timeAndAttendenceService.getLastClosedPayPeriod(this.contract.id);
        }),
        map(lastClosedPayPeriodResult => {
          if (lastClosedPayPeriodResult) {
            this.lastClosedPayPeriod = lastClosedPayPeriodResult;
          }
          this.debugLog('[ACCRUAL] last closed period fetched', this.lastClosedPayPeriod);
        })
      )
      .subscribe(() => this.estimateAccrual(absenceType));
  }

  public estimateAccrual(absenceType: string) {
    if (this.isEss && this.currentEvent && absenceType) {
      this.debugLog('[ACCRUAL] starting estimation with workcycle', this.workcycleMap);
      let monthsDiff: number = 0;
      let isOutOfRange: boolean = false;
      let isConfigNotFound: boolean = false;
      let isConfigNotUsable: boolean = false;
      if (!this.accrualConfig) {
        this.currentEvent.duration.before = 0;
        isConfigNotFound = true;
        this.debugLog('[ACCRUAL] no accrual config found for ' + absenceType + ' => config_not_found');
      } else if (!this.lastClosedPayPeriod || !this.lastClosedPayPeriod.executionDate) {
        this.currentEvent.duration.before = 0;
        isOutOfRange = true;
        this.debugLog('[ACCRUAL] missing last closed period execution date => out_of_range', this.lastClosedPayPeriod);
      } else {
        monthsDiff = moment(this.currentEvent.attributes.end).startOf('month')
          .diff(moment(this.lastClosedPayPeriod.endDate).add(1, 'month').startOf('month'), 'months');
        this.debugLog('[ACCRUAL] calculate month diff between end of absence and end of last closed period: ' + monthsDiff);
        if (monthsDiff < -1 || monthsDiff > 12 || !this.isInWorkCycle()) {
          this.currentEvent.duration.before = 0;
          isOutOfRange = true;
          this.debugLog('[ACCRUAL] month diff < -1 or > 12 or absence not in workcycle => out_of_range');
        } else {
          if (!this.accrualConfig.forecast) {
            this.currentEvent.duration.before = 0;
            isConfigNotUsable = true;
            this.debugLog('[ACCRUAL] missing forecast in accrual config => config_not_usable');
          }
        }
      }

      // todo achieve this calculation when slider is moved
      this.computeDayFactor();

      const getAbsenceDuration = this.timeAndAttendenceService.getAbsenceDuration(this.contract.id,
        this.currentEvent.attributes.begin, this.currentEvent.attributes.beginDayfactor,
        this.currentEvent.attributes.end, this.currentEvent.attributes.endDayfactor, absenceType);
      const estimateAbsenceDeltasOrNoop = isOutOfRange || isConfigNotFound || isConfigNotUsable ? of(null) : this.timeAndAttendenceService
        .estimateAbsenceDeltas(this.contract.id, absenceType, this.lastClosedPayPeriod.executionDate,
          moment(this.lastClosedPayPeriod.endDate).add(1, 'day').format(DATE_FORMAT),
          {
            ranges: getRangesForEstimation(this.lastClosedPayPeriod.endDate, 12),
            uiEvents: this.getUiEventsForEstimation()
          });
      forkJoin(getAbsenceDuration, estimateAbsenceDeltasOrNoop)
        .pipe(
          finalize(() => {
            if (isConfigNotFound) {
              this.currentEvent.duration.status = ACCRUAL_STATUS_CONFIG_NOT_FOUND;
            } else if (isConfigNotUsable) {
              this.currentEvent.duration.status = ACCRUAL_STATUS_CONFIG_NOT_USABLE;
            } else if (isOutOfRange) {
              this.currentEvent.duration.status = ACCRUAL_STATUS_OUT_OF_RANGE;
            } else {
              this.currentEvent.duration.status = ACCRUAL_STATUS_ESTIMATED;
            }
          })
        )
        .subscribe(([absenceDurationResult, estimateAbsenceDeltasResult]: Array<any>) => {
          this.debugLog('[ACCRUAL] absence duration fetched', absenceDurationResult);
          this.debugLog('[ACCRUAL] estimation deltas fetched', estimateAbsenceDeltasResult);
          const isAccrualConfigInDays: boolean = this.isAccrualConfigInDays(this.accrualConfig);
          if (!isOutOfRange && !isConfigNotFound && !isConfigNotUsable) {
            const beforeEstimation = this.estimateBefore(this.accrualConfig, estimateAbsenceDeltasResult, monthsDiff);
            this.currentEvent.duration.before = beforeEstimation.before;
            this.currentEvent.duration.beforeBeginDate = beforeEstimation.beforeBeginDate;
            if (!beforeEstimation.beforeBeginDate) {
              isConfigNotFound = true;
              this.debugLog('[ACCRUAL] before estimation not available => config_not_found');
            }
          }
          this.currentEvent.duration.current = isAccrualConfigInDays ? absenceDurationResult.days : absenceDurationResult.hours;
          this.currentEvent.duration.after = scale(this.currentEvent.duration.before - this.currentEvent.duration.current);
          this.debugLog('[ACCRUAL] calculate accruals with estimation - absence duration (' +
            this.currentEvent.duration.before + '-' + this.currentEvent.duration.current + ') => ' + this.currentEvent.duration.after);
        });
    }
  }

  private computeDayFactor() {
    if (this.isSingleDay(this.currentEvent)) {
      this.currentEvent.singleDateRange = this.currentEvent.singleDateRange.map(item => +item);
      this.currentEvent.attributes.beginDayfactor = this.currentEvent.singleDateRange[0].toFixed(5);
      this.currentEvent.attributes.endDayfactor = (1 - this.currentEvent.singleDateRange[1]).toFixed(5);
    } else {
      this.currentEvent.beginDateRange = this.currentEvent.beginDateRange.map(item => +item);
      this.currentEvent.endDateRange = this.currentEvent.endDateRange.map(item => +item);
      this.currentEvent.attributes.beginDayfactor = this.currentEvent.beginDateRange[0].toFixed(5);
      this.currentEvent.attributes.endDayfactor = (1 - this.currentEvent.endDateRange[1]).toFixed(5);
    }
  }

  public isAccrualConfigInDays(accrualConfig: any): boolean {
    return accrualConfig && accrualConfig.unit === 'days';
  }

  private estimateBefore(acccrualCfg: any, deltas: Array<any>, monthsDiff: number): { before: number, beforeBeginDate: string } {
    if (!acccrualCfg || !acccrualCfg.forecast || !deltas) {
      return {before: 0, beforeBeginDate: null};
    }
    const isAccrualConfigInDays = this.isAccrualConfigInDays(acccrualCfg);
    // sort deltas by beginDate (zero indexed)
    deltas.sort((delta1, delta2) => {
      return moment(delta1.beginDate).diff(delta2.beginDate);
    });
    let before: number = null;
    let beforeBeginDate: string = null;
    // forcecast looks like {"Month0": "{{SIC0}}","Month1": "{{SIC1}}","Month2": "{{SIC2}}","Month3": "{{SIC3}}"...
    const monthStartIndex = monthsDiff + 1;
    // find minimum pay accu value (with delta applied) for the futur periods
    this.debugLog('[ACCRUAL] find min value of pay values with delta applied');
    for (let monthIndex = monthStartIndex; monthIndex <= 12; monthIndex++) {
      let forecast: string = acccrualCfg.forecast[`Month${monthIndex}`];
      if (!forecast) {
        this.debugLog('[ACCRUAL]  L for Month' + monthIndex + ' forecast is not found => config_not_found');
        return {before: 0, beforeBeginDate: null};
      }
      // extract pay accu name from forcecast value: {{SIC1}}
      forecast = forecast.substr(2, forecast.length - 4);
      const payAccuValue = acccrualCfg.values[forecast];
      if (payAccuValue === null || payAccuValue === undefined) {
        this.debugLog('[ACCRUAL]  L for Month' + monthIndex + ' pay value is not found => config_not_found');
        return {before: 0, beforeBeginDate: null};
      }
      const deltaOfMonth = deltas[monthIndex];
      const payAccuValueWithDelta = payAccuValue + (isAccrualConfigInDays ? deltaOfMonth.deltaInDays : deltaOfMonth.deltaInHours);
      this.debugLog('[ACCRUAL]  L for Month' + monthIndex + ' forecast is ' + forecast + ', pay value is ' +
        payAccuValue + ', delta is ' + deltaOfMonth.deltaInDays + 'd/' + deltaOfMonth.deltaInHours + 'h => ' + payAccuValueWithDelta);
      if (before == null || payAccuValueWithDelta < before) {
        before = payAccuValueWithDelta;
        beforeBeginDate = deltaOfMonth.beginDate;
        this.debugLog('[ACCRUAL]    L is a candidate for estimate (' + deltaOfMonth.beginDate + ')');
      }
    }
    this.debugLog('[ACCRUAL] in all candidates, take estimation of ' + beforeBeginDate + ' => ' + before);
    return {
      before: before == null ? 0 : scale(before),
      beforeBeginDate: beforeBeginDate
    };
  }

  private debugLog(message?: any, ...optionalParams: any[]) {
    if (this.debugAccrualEstimation) {
      console.log(message, optionalParams);
    }
  }

  private getUiEventsForEstimation(): Array<any> {
    const uiEvents = [];
    this.newEvents.forEach((event) => {
      if (event.type === 'empAbsence'
        && (!this.currentEvent.id || this.currentEvent.id !== event.id)
        && this.currentEvent.attributes.type === event.attributes.type) {
        uiEvents.push({
          beginDate: event.attributes.begin,
          beginDayFactor: event.attributes.beginDayfactor,
          endDate: event.attributes.end,
          endDayFactor: event.attributes.endDayfactor,
          creationDate: moment().toISOString(),
          eventType: 'CREATE',
          absenceType: event.attributes.type
        });
      }
    });

    return uiEvents;
  }

  public canTakeLeave() {
    return !this.isEss
      || !this.accrualConfig
      || !this.currentEvent
      || !this.currentEvent.duration
      || this.currentEvent.duration.after >= this.accrualConfig.limit2;
  }

  public cantSubmitReadOnlyRequired() {
    if (this.repAbsenceGroupZoneDescriptor) {
      return this.repAbsenceGroupZoneDescriptor.zones.some(zone => zone.isRequired && zone.readOnly);
    }
    return false;
  }

  public eventChanged(newEvent: any) {
    newEvent.view.duration = this.getDuration(newEvent);

    if (this.parentTypes[newEvent.attributes.type] === 'repAbsence') {
      const sameTimeEvents = this.checkSameTime(newEvent);

      if (sameTimeEvents.length) {
        this.modalService.confirm({
          'title': 'DIALOG.events_overlapping',
          'text': 'DIALOG.events_overlapping?',
          'type': 'warning'
        }).then((confirmed: boolean) => {
          if (confirmed) {
            sameTimeEvents.forEach((event: any) => this.markAsDeleted(event));
          }
        });
      }
    }

    this.markAsModified(newEvent);
  }

  public deleteEvent(event: any) {
    this.markAsDeleted(event);
    this.currentEvent = null;
  }

  public getTitleModal(): string {
    if (this.preview) {
      return this.translate.instant('PAYROLL_PREPARE.view_event');
    }
    if (this.currentEvent && this.currentEvent.id) {
      return this.translate.instant('PAYROLL_PREPARE.edit_event');
    }
    return this.translate.instant('PAYROLL_PREPARE.add_event');
  }

  public onSubmit(): Observable<ModalCommand> {
    if (this.currentEvent) {
      if (this.repAbsenceGroupZoneDescriptor) {
        this.currentEvent.attributes.values = DataTransformer
          .valuesForZones(this.repAbsenceGroupZoneDescriptor.zones, this.currentEvent.attributes);
      }

      this.computeDayFactor();

      Object.assign(this.currentEvent.attributes, {
        cliCoreID: this.cliCoreID,
        name: this.currentEvent.attributes.type
      });

      let currentEventTransId;

      if (this.currentEvent.attributes.name) {
        currentEventTransId = this.eventTypes.find(item => item.name === this.currentEvent.attributes.name).transId;
      }

      if (this.currentEvent.parentType === 'repAbsence') {
        this.currentEvent.type = 'empAbsence';
        this.currentEvent.attributes.transid = currentEventTransId;

        if (isPresent(this.currentEvent.id)) {
          this.markAsModified(this.currentEvent);
        } else {
          this.currentEvent.isNew = true;
          this.markAsNew(this.currentEvent);
        }
      } else if (this.currentEvent.parentType === 'repVariable') {
        const attributes = {
          begindate: this.currentEvent.attributes.begin,
          enddate: this.currentEvent.attributes.end,
          name: this.currentEvent.attributes.type,
          value: this.currentEvent.attributes.value,
          empContract: this.contract.id,
          evtype: 'TIME',
          cliCoreID: this.cliCoreID,
          transId: currentEventTransId,
          unitTransId: this.currentEvent.attributes.unitTransId,
          values: this.currentEvent.attributes.values
        };

        if (isPresent(this.currentEvent.id)) {
          this.markAsModified({id: this.currentEvent.id, type: 'empVariable', attributes});
        } else {
          this.markAsNew({attributes, type: 'empVariable'});
        }
      }

      this.resetForms();

      return of({
        state: ModalState.VALID,
        message: this.translate.instant('PAYROLL_RESULT.saved_success'),
        duration: 0
      });
    }
  }

  public onClose(eventpopup) {
    this.resetForms();
    eventpopup.close();
  }

  public resetForms(): void {
    this.createEventForm();
    this.currentEvent = null;
    this.repAbsenceGroupZoneDescriptor = null;
    this.repAbsenceZoneValues = null;
  }

  set singleDateRange(singleDateRange: any) {
    this.setDateRangeTime('singleDateRange', singleDateRange, 'begin');
  }

  get singleDateRange() {
    return this.currentEvent.singleDateRange;
  }

  set beginDateRange(beginDateRange: any) {
    this.setDateRangeTime('beginDateRange', beginDateRange, 'begin');
  }

  get beginDateRange() {
    return this.currentEvent.beginDateRange;
  }

  set endDateRange(endDateRange: any) {
    this.setDateRangeTime('endDateRange', endDateRange, 'end');
  }

  get endDateRange() {
    return this.currentEvent.endDateRange;
  }

  public setDateRangeTime(field: string, newRange: any, type: string): void {
    if (!this.workcycleMap.get(this.currentEvent.attributes[type])) {
      return;
    }

    const workedHours = this.workcycleMap.get(this.currentEvent.attributes[type]).attributes.hoursWorkedRef;
    this.currentEvent[field] = newRange;
    let minutes = Math.round((newRange[1] - newRange[0]) * workedHours * 60);

    const hours: number = Math.floor(minutes / 60);
    minutes = Math.round(minutes - hours * 60);

    if (+this.timeFields[field].hours !== +hours) {
      this.timeFields[field].hours = hours;
    }

    if (+this.timeFields[field].minutes !== +minutes) {
      this.timeFields[field].minutes = minutes;
    }
    if (this.isInWorkCycle()) {
      if (!this.isAccrualEstimating(this.currentEvent)) {
        this.currentEvent.duration = {
          before: 0,
          current: 0,
          after: 0,
          status: ACCRUAL_STATUS_PREPARE_ESTIMATION
        };
      }
      this.refreshAccrual(this.currentEvent.attributes.type);
    }
  }

  public setDateRange(field: string, hours: number, minutes: number, type: string, isReverse: boolean = false): void {
    if (!this.workcycleMap.get(this.currentEvent.attributes[type])) {
      return;
    }

    const workedHours = this.workcycleMap.get(this.currentEvent.attributes[type]).attributes.hoursWorkedRef;

    this.setTimeFields(field, minutes, hours);

    minutes = minutes / 60;
    hours = (+hours + +minutes);

    if (this.timeFields[field].minutes === '' || this.timeFields[field].hours === '') {
      return;
    }

    if (hours > workedHours) {
      setTimeout(() => {
        this.timeFields[field].hours = workedHours;
        this.timeFields[field].minutes = 0;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (hours < 0) {
      setTimeout(() => {
        this.timeFields[field].hours = 0;
        this.timeFields[field].minutes = 15;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (minutes < 0) {
      setTimeout(() => {
        this.timeFields[field].minutes = 0;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    if (minutes > 1) {
      setTimeout(() => {
        this.timeFields[field].minutes = 59;
        this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
      });

      return;
    }

    hours = hours / workedHours;

    const slicedRange = this.currentEvent[field].slice();
    if (isReverse || (slicedRange[1] === 1 && slicedRange[0] !== 0)) {
      slicedRange[0] = parseFloat((slicedRange[0] + ((slicedRange[1] - slicedRange[0]) - hours)).toFixed(5));
    } else {
      slicedRange[1] = parseFloat((slicedRange[1] - ((slicedRange[1] - slicedRange[0]) - hours)).toFixed(5));
      if (slicedRange[1] > 1) {
        slicedRange[0] = parseFloat((slicedRange[0] - (slicedRange[1] - 1)).toFixed(5));
        slicedRange[1] = 1;
      }
    }

    this.currentEvent[field] = slicedRange;
  }

  public setTimeFields(field: string, minutes: number, hours: number) {
    if (!this.timeFields[field]) {
      return;
    }

    if (+this.timeFields[field].hours !== +hours) {
      this.timeFields[field].hours = hours;
    }

    if (+this.timeFields[field].minutes !== +minutes) {
      this.timeFields[field].minutes = minutes;
    }
  }

  public checkDateRange(field: string, type: string) {
    if (!this.timeFields[field]) {
      return;
    }

    if (this.timeFields[field].minutes === '') {
      this.timeFields[field].minutes = 0;
    }

    if (this.timeFields[field].hours === '') {
      this.timeFields[field].hours = 0;
    }

    const minutes = this.timeFields[field].minutes / 60;
    const hours = (+this.timeFields[field].hours + +minutes);

    if (hours < 0.25) {
      this.timeFields[field].hours = 0;
      this.timeFields[field].minutes = 15;
      this.setDateRange(field, this.timeFields[field].hours, this.timeFields[field].minutes, type);
    }
  }

  public getRangeDurationLabel(dateRange: number[], type: string): string {
    if (!this.workcycleMap.get(this.currentEvent.attributes[type])) {
      return;
    }
    const workedHours = this.workcycleMap.get(this.currentEvent.attributes[type]).attributes.hoursWorkedRef;
    let hours = (dateRange[1] - dateRange[0]) * workedHours * 100;
    const percent = Math.round((dateRange[1] - dateRange[0]) * 100);
    hours = Math.round(hours) / 100;
    if (hours === workedHours) {

      return `${this.translate.instant('PAYROLL_PREPARE.full_day')}`;
    } else {

      return `${this.translate.instant('PAYROLL_PREPARE.percent_of_day', {
        percent: `${percent}`
      })}`;
    }
  }

  public isSingleDay(event: any): boolean {
    return event.attributes.begin === event.attributes.end;
  }

  public getRangeStep(type: string): number {
    const day = this.workcycleMap.get(this.currentEvent.attributes[type]);

    return day && day.attributes.rangeStep || 0;
  }

  public getStep() {
    return this.isHalfDayAllowed() ? 0.5 : this.getRangeStep('begin') / 4;
  }

  public isEmptyRange(type: string) {
    const day = this.workcycleMap.get(this.currentEvent.attributes[type]);

    return day && day.attributes.isEmptyHours;
  }

  public getEventViewOpts(types: any[], event: any) {
    let color: string = WHITE;
    let borderColor: string = '';
    let borderStyle: string = 'solid';
    let transId: string = event.attributes.transId || event.attributes.transid;
    const readOnly: boolean = !types.some(type => type.attributes.name === event.attributes.name);
    if (event.type === 'empAbsence') {
      color = COLOR6;
      if (event.attributes.dirty) {
        borderColor = COLOR3;
        borderStyle = 'dashed';
        color = WHITE;
      } else if (event.attributes.status) {
        color = 'transparent';
        switch (event.attributes.status) {
          case 'CREATE':
            borderColor = COLOR3;
            break;
          case 'UPDATE':
            borderColor = COLOR4;
            break;
          case 'CANCELLATION':
            borderColor = COLOR5;
            break;
          default:
            break;
        }
      }
    } else if (event.type === 'empVariable') {
      color = COLOR7;
      types.find(type => {
        if (type.attributes.name === event.attributes.name) {
          if (type.attributes.familyTransId === 'Absences') {
            color = COLOR6;
          } else if (type.attributes.familyTransId === 'Presence') {
            color = COLOR7;
          }
          transId = type.attributes.transId;

          return true;
        }

        return false;
      });
    }


    return {color, transId, duration: this.getDuration(event), borderColor, borderStyle, readOnly};
  }

  public getDuration(event: any, unit: string = 'hours', accrualType?: string): number {
    let duration = 0;
    if (event.attributes.begin) {
      const diff = moment(event.attributes.end).diff(event.attributes.begin, 'days') + 1;
      const dayDuration: moment.Moment = moment(event.attributes.begin);

      let day = this.workcycleMap.get(dayDuration.format(DATE_FORMAT));
      if (day && (!accrualType || accrualType.includes(day.attributes.typeTransId))) {
        if (unit === 'hours') {
          duration -= event.attributes.beginDayfactor * day.attributes.hoursWorkedRef;
        } else {
          duration += (1 - event.attributes.beginDayfactor);
        }
      }

      for (let i = 0; i < diff; i++) {
        day = this.workcycleMap.get(dayDuration.format(DATE_FORMAT));
        if (day && (!accrualType || accrualType.includes(day.attributes.typeTransId))) {
          if (unit === 'hours') {
            duration += day.attributes.hoursWorkedRef;
          } else {
            duration++;
          }
        }
        dayDuration.add(1, 'day');
      }

      if (day && (!accrualType || accrualType.includes(day.attributes.typeTransId))) {
        if (unit === 'hours') {
          duration -= event.attributes.endDayfactor * day.attributes.hoursWorkedRef;
        } else {
          duration = duration - 1 - event.attributes.endDayfactor;
        }
      }
    }
    return Math.round(duration * 100) / 100;
  }

  public getCalendarData() {
    if (!this.contract) {
      return;
    }
    const currentRange = this.getDateRange();

    this.loading = true;

    this.absences = [];
    this.presences = [];
    this.workcycle = [];
    this.cd.markForCheck();
    forkJoin(
      this.getAbsenceList(currentRange),
      this.getVariablesList(currentRange)
    )
      .pipe(
        tap(([absences, variables]: Array<any>) => {
          let begin: moment.Moment = moment(currentRange.begin);
          let end: moment.Moment = moment(currentRange.end);
          this.absences = absences;
          this.presences = variables;

          [...absences, ...variables].forEach(event => {
            if (begin.isAfter(event.attributes.begin)) {
              begin = moment(event.attributes.begin);
            }
            if (end.isBefore(event.attributes.end)) {
              end = moment(event.attributes.end);
            }
          });
          currentRange.begin = begin.format(DATE_FORMAT);
          currentRange.end = end.format(DATE_FORMAT);
        }),
        switchMap(() => this.getWorkCycle(currentRange)),
        finalize(() => {
          this.loading = false;
          this.workCycleLoaded = true;
          this.cd.markForCheck();
        })
      )
      .subscribe((workcycle: Array<any>) => {
        this.workcycle = workcycle;
        this.absences.forEach(event => event.view = this.getEventViewOpts(this.eventTypes || [], event));
        this.presences.forEach(event => event.view = this.getEventViewOpts(this.eventTypes || [], event));
      });
  }

  public getAbsenceList(dateRange: DateRange) {

    return forkJoin(
      this.timeAndAttendenceService.getEmployeeAbsence(this.contract.id, dateRange.begin, dateRange.end, this.isEss)
        .pipe(map(result => {
          if (Array.isArray(result)) {
            return result;
          }

          return result ? result.data : [];
        })),
      this.timeAndAttendenceService.getPendingAbsences(this.contract.id, dateRange.begin, dateRange.end)
    ).pipe(
      map(([absences, pendingAbsences]) => [...absences, ...pendingAbsences])
    );
  }

  public getVariablesList(dateRange: DateRange) {

    return this.employeeVariablesService.getEmployeeVariables(this.contract.id, dateRange.begin, dateRange.end, this.isEss)
      .pipe(
        map(result => {
          if (Array.isArray(result)) {
            return result;
          }

          return result ? result.data : [];
        }),
        map(data => data.filter(item => item.attributes.evtype === 'TIME'))
      );
  }

  public getWorkCycle(dateRange: DateRange) {

    return this.timeAndAttendenceService
      .getWorkCycle(this.contract.id, dateRange.begin, dateRange.end, this.isEss ? ProfileMode.ESS : ProfileMode.CLASSIC)
      .pipe(
        map(result => {
          result.data.forEach((item) => {
            if (item.attributes.hoursWorkedRef) {
              item.attributes.rangeStep = 1 / item.attributes.hoursWorkedRef;
              item.attributes.isEmptyHours = false;
            } else {
              item.attributes.isEmptyHours = true;
              item.attributes.rangeStep = 0;
            }
            switch (item.attributes.typeTransId) {
              case 'OPE':
              case 'WOR':
                const isPartialTime = item.attributes.hoursWorked === 0;
                item.color = isPartialTime ? COLOR8 : WHITE;
                item.textColor = isPartialTime ? WHITE : COLOR1;
                break;
              case 'SUN':
              case 'SAT':
              case 'BAN':
              case 'OFF':
                item.color = COLOR2;
                item.textColor = WHITE;
                break;
              default:
                item.color = 'red';
                item.textColor = 'black';
                break;
            }

            this.workcycleMap.set(item.attributes.date, item);
          });
          this.workCycleIsSpecified = true;

          return result.data;
        }),
        catchError(_ => {
          this.workCycleIsSpecified = false;

          return [];
        })
      );
  }

  public getEventTypes() {

    return this.timeAndAttendenceService.getPresenceAndAbsenceTypes(this.cliRegId, this.isEss)
      .pipe(
        map(result => {
          this.eventTypes = result.data;
          this.eventTypes.forEach(item => {
            this.parentTypes[item.attributes.name] = item.type;
          });
        }),
        map(() => this.computeNeededTransValues()),
        switchMap(neededTransIds => {
          if (neededTransIds && neededTransIds.length > 0) {
            return this.translate.get(neededTransIds);
          }
          return of(null);
        }),
        map(transValues => this.sortByTransValues(transValues))
      );
  }

  public computeNeededTransValues() {
    const neededTransIds: Array<string> = new Array<string>();
    if (this.eventTypes) {
      this.eventTypes.forEach(type => {
        neededTransIds.push(type.attributes.familyTransId);
        neededTransIds.push(type.attributes.transId);
      });
    }
    return neededTransIds;
  }

  public sortByTransValues(transValues) {
    this.types = {};
    this.groupedTypes = {};
    if (transValues) {
      this.eventTypes.sort((type1, type2) => {
        const type1FamilyTransValue =
          transValues[type1.attributes.familyTransId] || type1.attributes.familyTransId;
        const type2FamilyTransValue =
          transValues[type2.attributes.familyTransId] || type2.attributes.familyTransId;
        const familyTransValueComparison = type1FamilyTransValue.localeCompare(type2FamilyTransValue);
        if (familyTransValueComparison === 0) {
          const type1TransValue = transValues[type1.attributes.transId] || type1.attributes.transId;
          const type2TransValue = transValues[type2.attributes.transId] || type2.attributes.transId;
          return type1TransValue.localeCompare(type2TransValue);
        }
        return familyTransValueComparison;
      });

      this.types = groupBy(this.eventTypes, element => at(element, 'attributes.familyTransId'));
      this.groupedTypes = groupBy(this.eventTypes, element => at(element, 'type'));

      this.categorySelectList = Object
        .keys(this.types)
        .map(key => ({
          id: key,
          transId: key
        }));

      Object.keys(this.groupedTypes)
        .forEach((groupType: string) => this.groupedTypes[groupType]
          .forEach((groupElement: any) => Object.assign(groupElement, groupElement.attributes)));

      for (const keys in this.groupedTypes) {
        this.groupedTypes[keys].sort((type1, type2) => {
          return transValues[type1.attributes.transId].localeCompare(transValues[type2.attributes.transId]);
        });
      }
    }
  }

  public getDateRange(): DateRange {
    const currentDate: string = this.viewDate;

    return {
      begin: moment(currentDate).add(-1, 'weeks').format(DATE_FORMAT),
      end: moment(currentDate).add(1, 'months').add(1, 'weeks').format(DATE_FORMAT)
    };
  }

  public getRepReportTemplate(reportName: string) {
    this.timeAndAttendenceService
      .getRepReport(this.cliRegId, reportName)
      .subscribe(result => {
        const response = result.data;
        const report = JSON.parse(response.attributes.template);
        report.columns = Object.keys(report.lines[0]);
        report.values = {};
        response.attributes.accus.split(';').forEach(accu => report.values[accu] = '-');
        this.getReportAccus(report);

        if (reportName === 'RepAccu1') {
          this.leftReport = report;
        } else if (reportName === 'RepAccu2') {
          this.rightReport = report;
        } else if (reportName === 'RepAccu3') {
          this.rightReport = report;
        }
      });
  }

  public getReportAccus(report: any) {
    const searchStr: string = Object.keys(report.values).map(name => `name=${name}`).join('&');

    this.timeAndAttendenceService
      .getLastAccus(this.contract.id, searchStr)
      .subscribe(result => {
        result.data.forEach(item => report.values[item.attributes.name] = item.attributes.value);
      });
  }

  public getTranslation(column, values) {
    return column.translate
      ? this.translate.instant(column.value || ' ', values)
      : this.translateParser.interpolate(column.value, values);
  }

  public checkDates() {
    this.isBeginBeforeEnd = this.isBeginBeforeEnd = moment(this.currentEvent.attributes.begin)
      .isSameOrBefore(this.currentEvent.attributes.end);

    return this.isFormValid();
  }

  public isFormValid() {
    return this.eventForm.valid
      && this.isBeginBeforeEnd
      && this.getDatePickerValid()
      && !this.isMaximumTime
      && this.isEventTypeFilled
      && this.generatedSimpleForm
      && this.generatedSimpleForm.form.valid;
  }

  public checkValues() {
    const datas = this.generatedSimpleForm.getModifiedCardsData();
    if (datas) {
      this.currentEvent.attributes.values = datas.values;
    }
  }

  public submitForm(modal) {
    this.checkValues();
    if (this.currentEvent.type === 'repAbsence') {
      const sameTimeEvents = this.checkSameTime(this.currentEvent);
      if (sameTimeEvents.length) {
        this.modalService.confirm({
          'title': 'DIALOG.events_overlapping',
          'text': 'DIALOG.events_overlapping?',
          'type': 'warning'
        }).then((confirmed: boolean) => {
          if (confirmed) {
            sameTimeEvents.forEach((event: any) => this.markAsDeleted(event));
            modal.submit();
          }
        });

        return;
      }
    }

    modal.submit();
  }

  public checkInputValue() {
    if (this.shouldInputAppear()) {
      const ms = moment(this.currentEvent.attributes.end).diff(moment(this.currentEvent.attributes.begin));
      if (this.currentEvent.attributes.unitTransId === 'Days') {
        const days = moment.duration(ms).asDays() + 1;
        this.isMaximumTime = this.input.normalNumber > days;
      } else if (this.currentEvent.attributes.unitTransId === 'Hours') {
        const hours = moment.duration(ms).asHours() + 24;
        this.isMaximumTime = this.input.normalNumber > hours;
      }
    }
  }

  public getDatePickerValid() {
    let datepickerValid = true;
    if (this.beginDate && this.beginDate) {
      datepickerValid = this.beginDate.isValid && this.endDate.isValid;
    }

    return datepickerValid;
  }

  public checkSameTime(event): Array<any> {
    return this.absences.filter((element) => {
      if (element.attributes.status) {
        return;
      }

      const endFactor = 1 - element.attributes.endDayfactor;
      const beginFactor = element.attributes.beginDayfactor;

      let eventSingleBeginFactor;
      let eventSingleEndFactor;
      let eventRangeBeginFactor;
      let eventRangeEndFactor;

      if (event.singleDateRange) {
        eventSingleBeginFactor = event.singleDateRange[0];
        eventSingleEndFactor = event.singleDateRange[1];
      } else {
        eventSingleBeginFactor = element.attributes.beginDayfactor;
        eventSingleEndFactor = 1 - element.attributes.endDayfactor;
      }

      if (event.beginDateRange && event.endDateRange) {
        eventRangeBeginFactor = event.beginDateRange[0];
        eventRangeEndFactor = event.endDateRange[1];
      } else {
        eventRangeBeginFactor = event.attributes.beginDayfactor;
        eventRangeEndFactor = 1 - event.attributes.endDayfactor;
      }

      return event.id !== element.id && (this.isSingleDay(event)
        && moment(event.attributes.begin).isSame(element.attributes.begin)
        && eventSingleEndFactor > beginFactor
        && eventSingleBeginFactor < endFactor
        || !this.isSingleDay(event)
        && moment(event.attributes.end).isSame(element.attributes.begin)
        && eventRangeEndFactor > beginFactor
        || !this.isSingleDay(event)
        && moment(event.attributes.begin).isSame(element.attributes.end)
        && eventRangeBeginFactor < endFactor
        || moment(event.attributes.begin).isBefore(element.attributes.end)
        && moment(event.attributes.end).isAfter(element.attributes.begin));
    });
  }

  public initData() {
    this.minDate = !this.isEss ? strToDate(this.contract.begindate) : moment().add(-1, 'months').toDate();
    this.maxDate = this.isEss ? moment().add(1, 'years').toDate() : undefined;
    this.employeeVariablesService.getContractPayPeriodRange(this.contract.id, this.payPeriodId, '', '', this.isEss ? ProfileMode.ESS : ProfileMode.CLASSIC)
      .subscribe(dateRange => {
        if (dateRange && dateRange.data) {
          this.currentPayPeriod = this.payPeriodId ? dateRange.data[1]
            : dateRange.data.find(item => item.attributes.status === 'ONGOING');
          this.nextPayPeriod = dateRange.data[2];
          this.viewDate = startOfMonth(this.currentPayPeriod.attributes.begindate);
          if (this.currentPayPeriod && this.viewDate) {
            this.fetchTableData();
          }
          this.cd.markForCheck();
        } else {
          this.viewDate = startOfMonth(new Date());
          this.fetchTableData();
          this.cd.markForCheck();
        }
      });
  }

  public checkStartDateContract() {
    if (!this.currentPayPeriod) {
      return;
    }
    this.isLongFuture = false;

    const periodDuration = this.currentPayPeriod.attributes.serie.duration;
    if (this.nextPayPeriod && moment(this.contract.begindate).isAfter(this.nextPayPeriod.attributes.begindate)) {
      this.employeeVariablesService.getPayPeriodByDateAndPaySerieDuration(this.contract.begindate, periodDuration).subscribe((response) => {
        if (response?.id) {
          this.payPeriodId = response.id;
          this.initData();

          this.isLongFuture = false;
        } else {
          this.isLongFuture = true;
        }
      });
    }
  }

  public createEventForm() {
    this.eventForm = this.formBuilder.group({
      beginDate: ['', GpValidators.validateISO8601Date],
      endDate: ['', GpValidators.validateISO8601Date],
      type: ['', ''],
      familyType: ['', ''],
      beginSlider: ['', ''],
      endSlider: ['', ''],
      duration: ['', ''],
      value: ['', ''],
      singleHours: ['', ''],
      singleMinutes: ['', ''],
      beginHours: ['', ''],
      beginMinutes: ['', ''],
      endHours: ['', ''],
      endMinutes: ['', ''],
      comment: ['', '']
    });
  }

  private resetFilterSpecificValues() {
    if (!this.shouldInputAppear()) {
      this.resetFormOf('value');
    }
  }

  private resetFormOf(controlName: string) {
    const control = this.eventForm.get(controlName);
    if (control) {
      control.clearValidators();
      control.reset('');
      control.updateValueAndValidity();
    }
  }

  public strToDate(value) {
    return strToDate(value);
  }

  public saveChanges(skipLoad: boolean = false) {
    const saved: Subject<boolean> = new Subject<boolean>();
    const requests = this.getRequests();

    if (requests.length > 0) {
      this.loading = true;
      forkJoin(requests)
        .pipe(
          finalize(() => {
            this.loading = false;
            saved.next(true);
            saved.complete();
            this.getPendingRequests();
          })
        )
        .subscribe(() => {
          if (!skipLoad) {
            this.getCalendarData();
          }
        });
    } else {
      saved.next(true);
      saved.complete();
    }

    return saved;
  }

  public getRequests(): Array<Observable<any>> {
    const requests: Array<Observable<any>> = [];

    this.deletedEvents.forEach((event) => {
      if (event.type === 'empAbsence') {
          requests.push(
            this.timeAndAttendenceService.triggerAbsenceWorkflowV2(
              this.getAbsenceData(event),
              this.cliRegId,
              'DELETE',
              this.isEss
            ).pipe(tap(() => {
              this.deletedEvents.delete(event);
              this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'));
            })));
      } else if (event.type === 'empVariable') {
        requests.push(this.employeeVariablesService.deleteVariableElement(event)
          .pipe(tap(() => this.deletedEvents.delete(event))));
      }
    });

    this.modifiedEvents.forEach((event) => {
      if (event.type === 'empAbsence') {
          requests.push(
            this.timeAndAttendenceService.triggerAbsenceWorkflowV2(
              this.getAbsenceData(event),
              this.cliRegId,
              'EDIT',
              this.isEss
            ).pipe(tap(() => {
              this.modifiedEvents.delete(event);
              this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'));
            })));
      } else if (event.type === 'empVariable') {
        requests.push(this.employeeVariablesService.updateVariableElement(event)
          .pipe(tap(() => this.modifiedEvents.delete(event))));
      }
    });

    this.newEvents.forEach((event) => {
      if (event.type === 'empAbsence') {
          requests.push(
            this.timeAndAttendenceService.triggerAbsenceWorkflowV2(
              this.getAbsenceData(event),
              this.cliRegId,
              'CREATE',
              this.isEss
            ).pipe(tap(() => {
              this.newEvents.delete(event);
              this.toastrService.success(this.translate.instant('WORKFLOWS.workflow_started'));
            })));
      } else if (event.type === 'empVariable') {
        requests.push(this.employeeVariablesService.createVariableElement(event)
          .pipe(tap(() => this.newEvents.delete(event))));
      }
    });

    return requests;
  }

  public isDirty(): boolean {
    return !!this.deletedEvents.size
      || !!this.modifiedEvents.size
      || !!this.newEvents.size;
  }

  public resetChanges(): void {
    this.deletedEvents.clear();
    this.newEvents.clear();
    this.modifiedEvents.clear();
  }

  public isHalfDayAllowed() {
    return this.currentEvent.attributes.allowedDurationType === 'HALFDAY';
  }

  public isFullDayAllowed() {
    return this.currentEvent.attributes.allowedDurationType === 'FULLDAY';
  }

  public getBeginDayFactor(dateRangeType: string) {
    return this.currentEvent[dateRangeType][0];
  }

  public getEndDayFactor(dateRangeType: string) {
    return this.currentEvent[dateRangeType][1];
  }

  public isMorning(dateRangeType: string) {
    return this.getBeginDayFactor(dateRangeType) === 0 && this.getEndDayFactor(dateRangeType) >= 0.5;
  }

  public isAfternoon(dateRangeType: string) {
    return this.getEndDayFactor(dateRangeType) === 1 && this.getBeginDayFactor(dateRangeType) <= 0.5;
  }

  public setMorning(dateRangeType: string) {
    const endDayFactor = this.getEndDayFactor(dateRangeType);
    if (this.isMorning(dateRangeType)) {
      this.currentEvent[dateRangeType] = endDayFactor < 0.5
        ? [0.5, 1]
        : [0.5, endDayFactor];
    } else {
      this.currentEvent[dateRangeType] = endDayFactor < 0.5
        ? [0, 0.5]
        : [0, endDayFactor];
    }
  }

  public setAfternoon(dateRangeType: string) {
    const beginDayFactor = this.getBeginDayFactor(dateRangeType);
    if (this.isAfternoon(dateRangeType)) {
      this.currentEvent[dateRangeType] = beginDayFactor > 0.5
        ? [0, 0.5]
        : [beginDayFactor, 0.5];
    } else {
      this.currentEvent[dateRangeType] = beginDayFactor > 0.5
        ? [0.5, 1]
        : [beginDayFactor, 1];
    }
  }

  public isMorningDisabled(dateRangeType: string) {
    return (dateRangeType === 'endDateRange' && this.isMorning(dateRangeType))
      || (dateRangeType === 'singleDateRange' && this.getEndDayFactor(dateRangeType) === 0.5
        && this.isMorning(dateRangeType));
  }

  public isAfternoonDisabled(dateRangeType: string) {
    return (dateRangeType === 'beginDateRange' && this.isAfternoon(dateRangeType))
      || (dateRangeType === 'singleDateRange' && this.getBeginDayFactor(dateRangeType) === 0.5
        && this.isAfternoon(dateRangeType));
  }


  private getAbsenceData(absence: AbsenceEvent): LeaveRequestData {
    let limitValue: number = null;

    if (this.isEss && absence.duration && this.accrualConfig) {
      limitValue = absence.duration.after >= this.accrualConfig.limit2 && absence.duration.after < this.accrualConfig.limit1
        ? 2 : 1;
    }

    return {
      isHr: !this.isEss,
      empAbsenceId: absence.id,
      cliEmpId: this.employee.id,
      contractId: this.contract.id,
      pendingEntityId: absence.attributes.pendingEntityId || null,
      beginDate: absence.attributes.begin,
      endDate: absence.attributes.end,
      beginDateFactor: absence.attributes.beginDayfactor,
      endDateFactor: absence.attributes.endDayfactor,
      firstName: this.employee.firstName,
      lastName: this.employee.lastName,
      name: absence.attributes.name,
      comment: absence.attributes.comment || '',
      transId: absence.attributes.transid,
      limitValue,
      values: absence.attributes.values
    };
  }

  private markAsDeleted(event: any) {
    if (!event.isNew) {
      this.deletedEvents.add(event);
    }

    if (event.type === 'empAbsence') {
      this.absences = this.absences.filter(e => e.id !== event.id);
    } else if (event.type === 'empVariable') {
      this.presences = this.presences.filter(e => e.id !== event.id);
    }
    this.newEvents.delete(event);
    this.modifiedEvents.delete(event);
    this.absences = [...this.absences];
    this.presences = [...this.presences];
  }

  private markAsNew(event: any) {
    event.id = (new Date()).getTime();
    this.newEvents.add(event);

    if (event.type === 'empAbsence') {
      this.absences.push(event);
    } else if (event.type === 'empVariable') {
      this.presences.push(event);
    }

    event.attributes.dirty = true;
    event.view = this.getEventViewOpts(this.eventTypes, event);
    this.absences = [...this.absences];
    this.presences = [...this.presences];
  }

  private markAsModified(event: any) {
    let e;
    if (event.type === 'empAbsence') {
      e = this.absences.find(absence => absence.id === event.id);
    } else if (event.type === 'empVariable') {
      e = this.presences.find(presence => presence.id === event.id);
    }

    if (!e) {
      return;
    }

    Object.assign(e, event);

    if (!this.newEvents.has(e)) {
      this.modifiedEvents.add(e);
    }

    e.attributes.dirty = true;
    e.view = this.getEventViewOpts(this.eventTypes, event);
    this.absences = [...this.absences];
    this.presences = [...this.presences];
  }

  private canNavigate(): Observable<boolean | Subject<boolean>> {
    if (this.isDirty()) {
      return from(
        this.modalService.dialogFromData('pending-changes')
          .then((choice: string) => {
            let canNavigate: boolean;

            switch (choice) {
              case 'save':
                return this.saveChanges(true);
              case 'dismiss':
                canNavigate = true;
                this.resetChanges();
                break;
              default:
                canNavigate = false;
                break;
            }

            return canNavigate;
          })
      );
    }
    return of(true);
  }

  private isInWorkCycle() {
    let isNotExist: boolean = false;
    range(
      this.currentEvent.attributes.begin,
      this.currentEvent.attributes.end
    ).by('days', (momentDay) => {
      if (!this.workcycleMap.has(momentDay.format(DATE_FORMAT))) {
        isNotExist = true;
      }
    });
    return !isNotExist;
  }

  onChangeCategoryEvent(absenceCategory: string) {
    if (!this.fromChangeTypeEvent) {
      this.fromCategoryChangeEvent = true;
      const type = this.types[absenceCategory];
      if (type) {
        this.eventForm.patchValue({
          type: type[0].attributes.name
        });
      }
    }
    this.fromCategoryChangeEvent = false;
  }

  private shouldInputAppear() {
    return this.currentEvent.parentType === 'repVariable' && this.input;
  }
}
